import { defineStore } from 'pinia';
import {
  imMessageList,
  imMessageRead,
  imMessageSend,
  imRoomClose,
  imRoomList,
  imRoomOpen,
} from '@/api/chat/im';
import { useUserStore } from './user';

/**
 * IM C2C（买家↔档口）会话 store，与客服 app 买家侧语义一致：
 * 买家侧未读=看了即清（selectRoom 即读）；已读回执=档口任一人看了即已读。
 * WS 事件（im_room/im_msg/im_read）由 socket store 转发到本 store。
 */
export const useImStore = defineStore({
  id: 'im',
  state: () => ({
    rooms: [] as API.ImRoomItem[],
    // 当前正在查看的房间（room 页面设置），查看中的房间不累计未读
    activeRoomId: 0,
    messages: {} as Record<number, API.ImMessageItem[]>,
    // 按房间记录是否还有更早消息（has_more=false 不再触发翻页）
    hasMore: {} as Record<number, boolean>,
    loaded: false,
  }),
  getters: {
    totalUnread(): number {
      return this.rooms.reduce((sum, r) => sum + (r.unread_num || 0), 0);
    },
  },
  actions: {
    myId(): number {
      return useUserStore().id;
    },

    async getRoomList(): Promise<API.ImRoomItem[]> {
      this.rooms = await imRoomList();
      this.loaded = true;
      if (this.activeRoomId) {
        const still = this.rooms.find(r => r.room_id === this.activeRoomId);
        if (!still) {
          this.activeRoomId = 0;
        }
      }
      return this.rooms;
    },

    /** 找或建与档口的房间并选中（档口页入口） */
    async openRoom(sellerId: number): Promise<API.ImRoomItem> {
      const room = await imRoomOpen(sellerId);
      const idx = this.rooms.findIndex(r => r.room_id === room.room_id);
      if (idx >= 0) {
        this.rooms[idx] = room;
      }
      else {
        this.rooms.unshift(room);
      }
      await this.selectRoom(room.room_id);
      return room;
    },

    /** 选中房间：加载消息并标记已读（买家侧看了即清） */
    async selectRoom(roomId: number): Promise<void> {
      this.activeRoomId = roomId;
      const page = await imMessageList(roomId, undefined, 20);
      this.messages[roomId] = page.list;
      this.hasMore[roomId] = page.has_more;
      const room = this.rooms.find(r => r.room_id === roomId);
      if (room && room.unread_num > 0) {
        imMessageRead(roomId).catch(() => {});
        room.unread_num = 0;
      }
    },

    /** 向前翻页（prepend）；has_more=false 到底，不再请求 */
    async loadMore(roomId: number): Promise<void> {
      if (this.hasMore[roomId] === false) return;
      const list = this.messages[roomId] || [];
      if (list.length === 0) return;
      const page = await imMessageList(roomId, list[0].id, 20);
      if (page.list.length > 0) {
        this.messages[roomId] = [...page.list, ...list];
      }
      this.hasMore[roomId] = page.has_more;
    },

    async sendText(roomId: number, content: string): Promise<void> {
      const message = await imMessageSend(roomId, content, 1);
      this.appendMessage(roomId, message);
    },

    async sendImage(roomId: number, url: string): Promise<void> {
      const message = await imMessageSend(roomId, url, 2);
      this.appendMessage(roomId, message);
    },

    appendMessage(roomId: number, message: API.ImMessageItem): void {
      const list = this.messages[roomId] || (this.messages[roomId] = []);
      if (!list.some(m => m.id === message.id)) {
        list.push(message);
      }
      const room = this.rooms.find(r => r.room_id === roomId);
      if (room) {
        room.last_msg_id = message.id;
        room.last_msg_time = message.send_time;
        room.last_msg = {
          msg_type: message.msg_type,
          content: message.msg_type === 2 ? '[图片]' : message.content,
          sender_type: message.sender_type,
        };
        room.status = 1;
      }
    },

    async closeRoom(roomId: number): Promise<void> {
      const room = await imRoomClose(roomId);
      const idx = this.rooms.findIndex(r => r.room_id === room.room_id);
      if (idx >= 0) {
        this.rooms[idx] = { ...this.rooms[idx], ...room };
      }
    },

    // ==================== WS 事件 ====================

    onRoomEvent(): void {
      this.getRoomList().catch(() => {});
    },

    /** 新消息：档口发来 → 未读+1（查看中不涨）+ 置顶 */
    onMessageEvent(data: { room_id: number, data: API.ImMessageItem }): void {
      const roomId = data.room_id;
      const message = data.data;
      const mine = message.sender_type === 'user' && message.sender_id === this.myId();
      if (this.messages[roomId]) {
        this.appendMessage(roomId, message);
      }
      const room = this.rooms.find(r => r.room_id === roomId);
      if (room) {
        if (!mine && roomId !== this.activeRoomId) {
          room.unread_num = (room.unread_num || 0) + 1;
        }
        room.last_msg_id = message.id;
        room.last_msg_time = message.send_time;
        room.last_msg = {
          msg_type: message.msg_type,
          content: message.msg_type === 2 ? '[图片]' : message.content,
          sender_type: message.sender_type,
        };
        const idx = this.rooms.indexOf(room);
        if (idx > 0) {
          this.rooms.splice(idx, 1);
          this.rooms.unshift(room);
        }
      }
      else {
        this.getRoomList().catch(() => {});
      }
    },

    /** 已读回执：档口已读到 cursor → 我的消息 is_read=1 */
    onReadEvent(data: { room_id: number, last_read_msg_id: number }): void {
      const list = this.messages[data.room_id];
      if (!list) return;
      const cursor = Number(data.last_read_msg_id);
      for (const m of list) {
        if (m.sender_type === 'user' && m.sender_id === this.myId() && Number(m.id) <= cursor) {
          m.is_read = 1;
        }
      }
    },
  },
});
